/**
 * Topology Service - Pure functions for network topology operations
 *
 * @remarks
 * Centraliza toda la lógica de recalculo geométrico y operaciones topológicas.
 * Todas las funciones son puras: no mutan estado, solo calculan y retornan.
 *
 * Invariantes topológicas que este servicio garantiza:
 * 1. Todo edge tiene fromNode y toNode válidos
 * 2. Primer vértice del edge coincide con fromNode
 * 3. Último vértice del edge coincide con toNode
 * 4. Vértices intermedios se conservan durante operaciones
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/services
 */

import type { CanonicalNode, CanonicalEdge, LineStringGeometry } from '../types';

/**
 * Resultado de recalcular geometría de un edge
 */
export interface RecomputedEdge {
  /** Edge con geometría actualizada */
  edge: CanonicalEdge;
  /** Nueva geometría calculada */
  geometry: LineStringGeometry;
  /** Nueva longitud aproximada (Haversine) */
  lengthMeters: number;
}

/**
 * Resultado de construir preview para mover nodo
 */
export interface MovedNodePreview {
  /** Nodo en nueva posición */
  node: CanonicalNode;
  /** Edges afectados con geometrías actualizadas */
  affectedEdges: CanonicalEdge[];
}

/**
 * Resultado de dividir un edge
 */
export interface SplitEdgeResult {
  /** Nuevo nodo creado en el punto de división */
  newNode: Omit<CanonicalNode, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'>;
  /** Primer edge (fromNode → newNode) */
  edge1: Omit<CanonicalEdge, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'>;
  /** Segundo edge (newNode → toNode) */
  edge2: Omit<CanonicalEdge, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'>;
}

// ============================================================================
// FUNCIONES PURAS DE RECALCULO GEOMÉTRICO
// ============================================================================

/**
 * Recalcula la geometría de un edge cuando uno de sus nodos se mueve
 *
 * @remarks
 * - Preserva vértices intermedios del edge
 * - Solo actualiza el primer vértice (si fromNode) o último (si toNode)
 * - No muta el edge original, retorna nueva geometría
 *
 * @param edge - Edge a recalcular
 * @param movedNodeId - ID del nodo que se movió
 * @param newPosition - Nueva posición [lng, lat] del nodo
 * @returns Edge con geometría actualizada
 *
 * @example
 * ```ts
 * const edge = { fromNode: { id: 'n1' }, toNode: { id: 'n2' }, geometry: {...} };
 * const result = recomputeEdgeGeometry(edge, 'n1', [-68.84, -32.89]);
 * // result.geometry tiene primer vértice actualizado
 * ```
 */
export function recomputeEdgeGeometry(
  edge: CanonicalEdge,
  movedNodeId: string,
  newPosition: [number, number]
): RecomputedEdge {
  // Clonar coordenadas actuales
  const coordinates = [...edge.geometry.coordinates] as [number, number][];

  // Actualizar primer o último vértice según qué nodo se movió
  if (edge.fromNode.id === movedNodeId) {
    // Nodo origen se movió → actualizar primer vértice
    coordinates[0] = newPosition;
  } else if (edge.toNode.id === movedNodeId) {
    // Nodo destino se movió → actualizar último vértice
    coordinates[coordinates.length - 1] = newPosition;
  } else {
    // El nodo movido no pertenece a este edge (error de lógica)
    console.warn(`[topology] Node ${movedNodeId} is not connected to edge ${edge.id}`);
    return {
      edge,
      geometry: edge.geometry,
      lengthMeters: edge.lengthMeters || 0,
    };
  }

  // Crear nueva geometría
  const newGeometry: LineStringGeometry = {
    type: 'LineString',
    coordinates,
  };

  // Calcular nueva longitud (aproximación Haversine)
  const lengthMeters = calculateLineStringLength(newGeometry);

  // Retornar edge actualizado (sin mutar original)
  return {
    edge: {
      ...edge,
      geometry: newGeometry,
      lengthMeters,
    },
    geometry: newGeometry,
    lengthMeters,
  };
}

/**
 * Construye preview completo para mover un nodo
 *
 * @remarks
 * Genera vista temporal del nodo en nueva posición y todos sus edges conectados
 * actualizados. Esta función es pura y no modifica estado.
 *
 * @param node - Nodo a mover
 * @param newPosition - Nueva posición [lng, lat]
 * @param connectedEdges - Todos los edges conectados al nodo
 * @returns Preview del nodo y edges afectados
 *
 * @example
 * ```ts
 * const preview = buildPreviewForMovedNode(
 *   node,
 *   [-68.84, -32.89],
 *   connectedEdges
 * );
 * // preview.node tiene nueva posición
 * // preview.affectedEdges tiene todos los edges actualizados
 * ```
 */
export function buildPreviewForMovedNode(
  node: CanonicalNode,
  newPosition: [number, number],
  connectedEdges: CanonicalEdge[]
): MovedNodePreview {
  // Nodo en nueva posición
  const movedNode: CanonicalNode = {
    ...node,
    geometry: {
      type: 'Point',
      coordinates: newPosition,
    },
  };

  // Recalcular geometría de todos los edges conectados
  const affectedEdges = connectedEdges.map((edge) => {
    const result = recomputeEdgeGeometry(edge, node.id, newPosition);
    return result.edge;
  });

  return {
    node: movedNode,
    affectedEdges,
  };
}

/**
 * Normaliza los extremos de un edge para que coincidan con sus nodos
 *
 * @remarks
 * Asegura invariante topológica: primer vértice = fromNode, último = toNode.
 * Usado antes de persistir para garantizar consistencia.
 *
 * @param edge - Edge a normalizar
 * @param fromNode - Nodo origen
 * @param toNode - Nodo destino
 * @returns Edge con extremos normalizados
 */
export function normalizeEdgeEndpoints(
  edge: CanonicalEdge,
  fromNode: CanonicalNode,
  toNode: CanonicalNode
): CanonicalEdge {
  const coordinates = [...edge.geometry.coordinates] as [number, number][];

  // Normalizar extremos
  coordinates[0] = fromNode.geometry.coordinates as [number, number];
  coordinates[coordinates.length - 1] = toNode.geometry.coordinates as [number, number];

  const normalizedGeometry: LineStringGeometry = {
    type: 'LineString',
    coordinates,
  };

  const lengthMeters = calculateLineStringLength(normalizedGeometry);

  return {
    ...edge,
    geometry: normalizedGeometry,
    lengthMeters,
  };
}

// ============================================================================
// OPERACIONES TOPOLÓGICAS COMPLEJAS
// ============================================================================

/**
 * Divide un edge en un punto específico, creando un nodo intermedio
 *
 * @remarks
 * Operación de split:
 * 1. Crea nuevo nodo en splitCoordinate
 * 2. Genera edge1: fromNode → newNode
 * 3. Genera edge2: newNode → toNode
 * 4. Preserva atributos del edge original en ambos nuevos edges
 *
 * El edge original debe ser eliminado después de crear los dos nuevos.
 *
 * @param edge - Edge a dividir
 * @param splitCoordinate - Coordenada [lng, lat] donde dividir
 * @returns Nodo nuevo y dos edges nuevos (sin IDs, para crear en backend)
 *
 * @example
 * ```ts
 * const result = splitEdgeAtPoint(edge, [-68.84, -32.89]);
 * // result.newNode es el nodo intermedio
 * // result.edge1 va desde el nodo origen hasta el nuevo nodo
 * // result.edge2 va desde el nuevo nodo hasta el nodo destino
 * ```
 */
export function splitEdgeAtPoint(
  edge: CanonicalEdge,
  splitCoordinate: [number, number]
): SplitEdgeResult {
  const coords = edge.geometry.coordinates as [number, number][];

  // Encontrar el índice donde insertar el punto de split
  // (por ahora, simplificamos: split en el medio geométrico)
  const splitIndex = findSplitIndex(coords, splitCoordinate);

  // Coordenadas del primer segmento: desde inicio hasta split
  const coords1 = [...coords.slice(0, splitIndex + 1), splitCoordinate];

  // Coordenadas del segundo segmento: desde split hasta final
  const coords2 = [splitCoordinate, ...coords.slice(splitIndex + 1)];

  // Crear nodo intermedio
  const newNode: Omit<CanonicalNode, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'> = {
    geometry: {
      type: 'Point',
      coordinates: splitCoordinate,
    },
    sourceType: 'manual',
    sourceReference: null,
    layer: edge.layer,
    intersectionType: null,
    isManualAuthority: true,
    status: 'active',
  };

  // Primer edge: fromNode → newNode
  const edge1: Omit<CanonicalEdge, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'> = {
    fromNode: edge.fromNode,
    toNode: null as any, // Se asignará después de crear newNode
    geometry: {
      type: 'LineString',
      coordinates: coords1,
    },
    lengthMeters: calculateLineStringLength({ type: 'LineString', coordinates: coords1 }),
    sourceType: 'manual',
    sourceReference: edge.sourceReference,
    roadClass: edge.roadClass,
    accessMode: edge.accessMode,
    oneway: edge.oneway,
    onewayDirection: edge.onewayDirection,
    layer: edge.layer,
    speedLimitKmh: edge.speedLimitKmh,
    typicalSpeedKmh: edge.typicalSpeedKmh,
    surfaceType: edge.surfaceType,
    lanes: edge.lanes,
    widthMeters: edge.widthMeters,
    parentEdgeId: null,
    isManualAuthority: true,
    status: 'active',
  };

  // Segundo edge: newNode → toNode
  const edge2: Omit<CanonicalEdge, 'id' | 'project' | 'createdBy' | 'createdAt' | 'updatedAt'> = {
    fromNode: null as any, // Se asignará después de crear newNode
    toNode: edge.toNode,
    geometry: {
      type: 'LineString',
      coordinates: coords2,
    },
    lengthMeters: calculateLineStringLength({ type: 'LineString', coordinates: coords2 }),
    sourceType: 'manual',
    sourceReference: edge.sourceReference,
    roadClass: edge.roadClass,
    accessMode: edge.accessMode,
    oneway: edge.oneway,
    onewayDirection: edge.onewayDirection,
    layer: edge.layer,
    speedLimitKmh: edge.speedLimitKmh,
    typicalSpeedKmh: edge.typicalSpeedKmh,
    surfaceType: edge.surfaceType,
    lanes: edge.lanes,
    widthMeters: edge.widthMeters,
    parentEdgeId: null,
    isManualAuthority: true,
    status: 'active',
  };

  return {
    newNode,
    edge1,
    edge2,
  };
}

/**
 * Encuentra el índice óptimo para insertar el punto de split
 *
 * @param coords - Coordenadas del LineString
 * @param splitPoint - Punto donde se quiere dividir
 * @returns Índice del segmento donde insertar
 */
function findSplitIndex(coords: [number, number][], splitPoint: [number, number]): number {
  // Por ahora, retornamos el índice del medio
  // TODO: Implementar búsqueda del segmento más cercano al splitPoint
  return Math.floor(coords.length / 2) - 1;
}

// ============================================================================
// UTILIDADES GEOMÉTRICAS
// ============================================================================

/**
 * Calcula la longitud de un LineString usando fórmula de Haversine
 *
 * @param geometry - Geometría LineString
 * @returns Longitud en metros
 */
export function calculateLineStringLength(geometry: LineStringGeometry): number {
  const coords = geometry.coordinates as [number, number][];
  if (coords.length < 2) return 0;

  let totalLength = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    totalLength += haversineDistance(coords[i], coords[i + 1]);
  }

  return totalLength;
}

/**
 * Calcula distancia entre dos puntos usando fórmula de Haversine
 *
 * @param point1 - [lng, lat]
 * @param point2 - [lng, lat]
 * @returns Distancia en metros
 */
export function haversineDistance(
  point1: [number, number],
  point2: [number, number]
): number {
  const R = 6371000; // Radio de la Tierra en metros
  const [lng1, lat1] = point1;
  const [lng2, lat2] = point2;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Convierte grados a radianes
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calcula el punto medio de un LineString (mid-point)
 *
 * @param geometry - Geometría LineString
 * @returns Coordenada [lng, lat] del punto medio
 */
export function calculateMidPoint(geometry: LineStringGeometry): [number, number] {
  const coords = geometry.coordinates as [number, number][];
  const totalLength = calculateLineStringLength(geometry);
  const halfLength = totalLength / 2;

  let accumulatedLength = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const segmentLength = haversineDistance(coords[i], coords[i + 1]);
    if (accumulatedLength + segmentLength >= halfLength) {
      // El punto medio está en este segmento
      const ratio = (halfLength - accumulatedLength) / segmentLength;
      return interpolatePoint(coords[i], coords[i + 1], ratio);
    }
    accumulatedLength += segmentLength;
  }

  // Fallback: retornar el último punto
  return coords[coords.length - 1];
}

/**
 * Interpola entre dos puntos
 *
 * @param p1 - Punto inicial [lng, lat]
 * @param p2 - Punto final [lng, lat]
 * @param ratio - Ratio de interpolación (0 = p1, 1 = p2)
 * @returns Punto interpolado [lng, lat]
 */
export function interpolatePoint(
  p1: [number, number],
  p2: [number, number],
  ratio: number
): [number, number] {
  const lng = p1[0] + (p2[0] - p1[0]) * ratio;
  const lat = p1[1] + (p2[1] - p1[1]) * ratio;
  return [lng, lat];
}

// ============================================================================
// PROYECCIÓN PUNTO-LINESTRING (Asociación de puntos a red)
// ============================================================================

/**
 * Resultado de proyectar un punto sobre un LineString
 */
export interface ProjectionResult {
  /** Coordenada proyectada sobre el LineString [lng, lat] */
  point: [number, number];
  /** Índice del segmento donde cayó la proyección (0-based) */
  segmentIndex: number;
  /** Parámetro t dentro del segmento (0 = inicio segmento, 1 = fin segmento) */
  t: number;
  /** Posición normalizada sobre todo el LineString (0.0 = inicio, 1.0 = fin) */
  positionOnEdge: number;
  /** Distancia perpendicular desde el punto original al LineString (metros) */
  distanceToEdge: number;
  /** Longitud acumulada hasta el punto de proyección (metros) */
  accumulatedLength: number;
}

/**
 * Lado del edge donde se encuentra un punto
 */
export type SideOfEdge = 'left' | 'right' | 'center';

/**
 * Proyecta un punto sobre un LineString y calcula información detallada
 *
 * @remarks
 * Algoritmo:
 * 1. Itera cada segmento del LineString
 * 2. Para cada segmento, calcula proyección perpendicular usando producto punto
 * 3. Si la proyección cae fuera del segmento (t < 0 o t > 1), usa el extremo más cercano
 * 4. Encuentra el segmento con menor distancia perpendicular
 * 5. Calcula positionOnEdge = longitud_acumulada / longitud_total
 *
 * @param point - Punto a proyectar [lng, lat]
 * @param lineString - Geometría LineString sobre la cual proyectar
 * @returns Información completa de la proyección
 *
 * @example
 * ```ts
 * const result = projectPointToLineString(
 *   [-68.84, -32.89],
 *   edge.geometry
 * );
 * console.log(result.positionOnEdge); // 0.65 (65% del edge)
 * console.log(result.distanceToEdge); // 3.2 metros
 * ```
 */
export function projectPointToLineString(
  point: [number, number],
  lineString: LineStringGeometry
): ProjectionResult {
  const coords = lineString.coordinates as [number, number][];

  if (coords.length < 2) {
    // Edge degenerado: retornar el único punto
    return {
      point: coords[0] || point,
      segmentIndex: 0,
      t: 0,
      positionOnEdge: 0,
      distanceToEdge: coords[0] ? haversineDistance(point, coords[0]) : 0,
      accumulatedLength: 0,
    };
  }

  const totalLength = calculateLineStringLength(lineString);

  let bestResult: ProjectionResult | null = null;
  let minDistance = Infinity;
  let accumulatedLengthToSegmentStart = 0;

  // Iterar cada segmento
  for (let i = 0; i < coords.length - 1; i++) {
    const segStart = coords[i];
    const segEnd = coords[i + 1];
    const segmentLength = haversineDistance(segStart, segEnd);

    // Calcular proyección perpendicular sobre el segmento
    const projection = projectPointToSegment(point, segStart, segEnd);

    if (projection.distance < minDistance) {
      minDistance = projection.distance;

      // Calcular longitud acumulada hasta el punto de proyección
      const lengthToProjection = accumulatedLengthToSegmentStart + projection.t * segmentLength;

      // Calcular posición normalizada sobre todo el edge
      const positionOnEdge = totalLength > 0 ? lengthToProjection / totalLength : 0;

      bestResult = {
        point: projection.point,
        segmentIndex: i,
        t: projection.t,
        positionOnEdge,
        distanceToEdge: projection.distance,
        accumulatedLength: lengthToProjection,
      };
    }

    accumulatedLengthToSegmentStart += segmentLength;
  }

  // Si no encontramos resultado (no debería pasar), retornar el primer punto
  return bestResult || {
    point: coords[0],
    segmentIndex: 0,
    t: 0,
    positionOnEdge: 0,
    distanceToEdge: haversineDistance(point, coords[0]),
    accumulatedLength: 0,
  };
}

/**
 * Proyecta un punto sobre un segmento de línea (entre dos puntos)
 *
 * @remarks
 * Usa el producto punto para encontrar la proyección perpendicular.
 * El parámetro t indica dónde cae la proyección:
 * - t < 0: antes del inicio del segmento (se usa el inicio)
 * - t > 1: después del fin del segmento (se usa el fin)
 * - 0 <= t <= 1: dentro del segmento (se usa la proyección)
 *
 * @param point - Punto a proyectar [lng, lat]
 * @param segStart - Inicio del segmento [lng, lat]
 * @param segEnd - Fin del segmento [lng, lat]
 * @returns { point, t, distance }
 */
function projectPointToSegment(
  point: [number, number],
  segStart: [number, number],
  segEnd: [number, number]
): { point: [number, number]; t: number; distance: number } {
  // Vector del segmento
  const dx = segEnd[0] - segStart[0];
  const dy = segEnd[1] - segStart[1];

  // Longitud del segmento al cuadrado (en coordenadas)
  const segLengthSq = dx * dx + dy * dy;

  if (segLengthSq === 0) {
    // Segmento degenerado (punto)
    return {
      point: segStart,
      t: 0,
      distance: haversineDistance(point, segStart),
    };
  }

  // Calcular parámetro t usando producto punto
  // t = ((point - segStart) · (segEnd - segStart)) / |segEnd - segStart|²
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point[0] - segStart[0]) * dx + (point[1] - segStart[1]) * dy) / segLengthSq
    )
  );

  // Punto proyectado
  const projectedPoint: [number, number] = [
    segStart[0] + t * dx,
    segStart[1] + t * dy,
  ];

  // Distancia desde el punto original al punto proyectado
  const distance = haversineDistance(point, projectedPoint);

  return {
    point: projectedPoint,
    t,
    distance,
  };
}

/**
 * Determina de qué lado del edge se encuentra un punto
 *
 * @remarks
 * Usa el producto cruz 2D para determinar la orientación:
 * - Positivo: el punto está a la izquierda del segmento (en dirección de viaje)
 * - Negativo: el punto está a la derecha
 * - Cero (o muy cercano): el punto está sobre el segmento
 *
 * La dirección se define desde fromNode hacia toNode.
 *
 * @param point - Punto a evaluar [lng, lat]
 * @param lineString - Geometría del edge
 * @param projection - Resultado de la proyección (para saber en qué segmento está)
 * @returns 'left' | 'right' | 'center'
 *
 * @example
 * ```ts
 * const projection = projectPointToLineString(stopCoords, edge.geometry);
 * const side = determineSideOfEdge(stopCoords, edge.geometry, projection);
 * console.log(side); // 'right' - parada sobre vereda derecha
 * ```
 */
export function determineSideOfEdge(
  point: [number, number],
  lineString: LineStringGeometry,
  projection: ProjectionResult
): SideOfEdge {
  const coords = lineString.coordinates as [number, number][];

  if (coords.length < 2) {
    return 'center';
  }

  // Usar el segmento donde cayó la proyección
  const segIndex = Math.min(projection.segmentIndex, coords.length - 2);
  const segStart = coords[segIndex];
  const segEnd = coords[segIndex + 1];

  // Producto cruz 2D: (segEnd - segStart) × (point - segStart)
  // = (dx_seg) * (dy_point) - (dy_seg) * (dx_point)
  const dxSeg = segEnd[0] - segStart[0];
  const dySeg = segEnd[1] - segStart[1];
  const dxPoint = point[0] - segStart[0];
  const dyPoint = point[1] - segStart[1];

  const crossProduct = dxSeg * dyPoint - dySeg * dxPoint;

  // Umbral para considerar "sobre la línea" (compensar errores de floating point)
  // En coordenadas geográficas, ~1e-8 grados es aproximadamente 1mm
  const threshold = 1e-7;

  if (Math.abs(crossProduct) < threshold) {
    return 'center';
  }

  // En sistema de coordenadas geográficas con lat hacia arriba:
  // crossProduct > 0 significa que el punto está a la izquierda del vector de dirección
  return crossProduct > 0 ? 'left' : 'right';
}

/**
 * Encuentra el punto en un LineString dado una posición normalizada
 *
 * @remarks
 * Operación inversa de projectPointToLineString.positionOnEdge
 * Dado un valor entre 0 y 1, encuentra la coordenada correspondiente.
 *
 * @param lineString - Geometría LineString
 * @param position - Posición normalizada (0.0 - 1.0)
 * @returns Coordenada [lng, lat] en esa posición
 *
 * @example
 * ```ts
 * const midPoint = getPointAtPosition(edge.geometry, 0.5);
 * const startPoint = getPointAtPosition(edge.geometry, 0);
 * const endPoint = getPointAtPosition(edge.geometry, 1);
 * ```
 */
export function getPointAtPosition(
  lineString: LineStringGeometry,
  position: number
): [number, number] {
  const coords = lineString.coordinates as [number, number][];

  if (coords.length < 2) {
    return coords[0] || [0, 0];
  }

  // Clampar posición a [0, 1]
  const clampedPosition = Math.max(0, Math.min(1, position));

  if (clampedPosition === 0) return coords[0];
  if (clampedPosition === 1) return coords[coords.length - 1];

  const totalLength = calculateLineStringLength(lineString);
  const targetLength = clampedPosition * totalLength;

  let accumulatedLength = 0;

  for (let i = 0; i < coords.length - 1; i++) {
    const segmentLength = haversineDistance(coords[i], coords[i + 1]);

    if (accumulatedLength + segmentLength >= targetLength) {
      // El punto está en este segmento
      const ratio = (targetLength - accumulatedLength) / segmentLength;
      return interpolatePoint(coords[i], coords[i + 1], ratio);
    }

    accumulatedLength += segmentLength;
  }

  // Fallback
  return coords[coords.length - 1];
}
